<template>
  <div class="bp-date-picker">
    <div class="functional-1 d-flex gap-3 mb-2" v-if="label">
      <span class="text-neutral-600">{{ label }}</span>
      <div class="text-negative-500" v-if="required">*</div>
      <bp-tooltip v-if="tooltip" :text="tooltip">
        <bp-icon size="sm" icon="fa-light fa-info-circle" />
      </bp-tooltip>
    </div>
    <div class="bp-date-picker__container position-relative">
      <bp-select
        v-model="range"
        :size="size"
        :options="rangeOptions"
        @change="selectRange"
      />

      <button :class="`bp-button-${size}`" @click.stop="toggle">
        <bp-icon icon="fa-regular fa-calendar-days" size="16" />
        <span class="px-3 functional-1">{{ selectedDates }}</span>
      </button>
    </div>

    <responsive-modal title="Período" v-model="modal" v-if="isMobile">
      <hr class="my-5" />
      <date-picker-calendar v-model="innerValue" @input="apply" />
    </responsive-modal>
    <date-picker-popper trigger="clickToToggle" :options="popperOptions" v-else>
      <div ref="popper" slot="reference" />

      <div slot="default" class="bp-date-picker__popper">
        <date-picker-calendar
          v-model="innerValue"
          ref="calendar"
          @input="apply"
        />
      </div>
    </date-picker-popper>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import Popper from "vue-popperjs";
import "vue-popperjs/dist/vue-popper.css";
import DatePickerCalendar from "./DatePickerCalendar.vue";
export default {
  components: {
    DatePickerCalendar,
    "date-picker-popper": Popper
  },
  props: {
    value: {
      type: Array,
      default: () => ["", ""]
    },
    size: {
      type: String,
      default: "md",
      validate: value => ["sm", "md", "lg"].includes(value)
    },
    label: {
      type: String,
      default: ""
    },
    required: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: ""
    },
    format: {
      type: String,
      default: "YYYY-MM-DD"
    }
  },
  data() {
    const rangeOptions = [
      { id: "", name: "Total" },
      { id: 1, name: "Hoje" },
      { id: 7, name: "Semana" },
      { id: 15, name: "Quinzena" },
      { id: 30, name: "Mês" },
      { id: 45, name: "45 dias" },
      { id: 90, name: "3 meses" },
      { id: 180, name: "6 meses" },
      { id: 365, name: "12 meses" },
      { id: "range", name: "Período", hide: true },
      { id: "day", name: "Dia", hide: true }
    ];
    return {
      innerValue: this.value,
      rangeOptions,
      range: "",
      modal: false,
      isMobile: window.innerWidth < 992,
      popperOptions: {
        placement: "bottom",
        modifiers: {
          offset: {
            offset: "0,10px"
          }
        }
      }
    };
  },
  computed: {
    selectedDates() {
      if (!this.innerValue[0] || !this.innerValue[1]) {
        return "-";
      }
      if (moment(this.innerValue[0]).isSame(this.innerValue[1], "day")) {
        return this.formatDate(this.innerValue[0]);
      }
      return `${this.formatDate(this.innerValue[0])} - ${this.formatDate(
        this.innerValue[1]
      )}`;
    }
  },
  methods: {
    toggle() {
      if (this.isMobile) {
        this.modal = !this.modal;
      } else {
        this.$refs.calendar.setStartAndEnd();
        this.$refs.popper.click();
      }
    },
    apply(v) {
      if (v[0] && v[1]) v = v.map(d => moment(d).format(this.format));

      this.$emit("input", v);
      this.setRange(v);
      this.toggle();
    },
    selectRange(opt) {
      if (!opt.id) {
        this.$emit("input", ["", ""]);
        return;
      }
      if (opt.id === "range" || opt.id === "day") {
        this.toggle();
        return;
      }
      const end = moment();
      const start = moment().subtract(opt.id - 1, "days");
      this.$emit("input", [start.format(this.format), end.format(this.format)]);
    },
    setRange(val) {
      let [start, end] = val;
      if (start && end) {
        const diff = moment(end).diff(start, "days");
        if (moment().isSame(end, "day") && diff === 0) {
          return (this.range = 1);
        }
        let range = this.rangeOptions.find(o => o.id === diff)?.id;
        if (!range) {
          range = moment(start).isSame(end, "day") ? "day" : "range";
        }
        return (this.range = range);
      }
      this.range = "";
    },

    formatDate(date) {
      return _.startCase(moment(date).format("DD MMM YY"));
    }
  },
  watch: {
    value: {
      handler(v) {
        this.innerValue = v;
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/buttons";
.bp-date-picker {
  width: min-content;
  .bp-date-picker__container {
    display: grid;
    border-radius: 4px;
    grid-template-columns: auto auto;
    ::v-deep {
      .bp-select {
        position: unset;
      }
      .bp-select__options {
        transform: translate(0, 44px) !important;
      }
      .bp-select__container {
        border-radius: 4px 0 0 4px;
        border-color: $neutral-300;
        border-right: 0px;
      }
      .bp-select__inner {
        width: 90px;
      }
    }
    button {
      border: 1px solid $neutral-300;
      border-radius: 0 4px 4px 0;
      font-weight: normal;
      background-color: unset;
      display: flex;
      align-items: center;
      transition: 0.2s border-color;
      white-space: nowrap;
    }
    &:focus-within {
      ::v-deep .bp-select__container {
        border-color: $primary-500;
      }
      button {
        border-color: $primary-500;
      }
    }
  }
  .bp-date-picker__popper {
    width: 340px;
    z-index: 1000;
    border-radius: var(--4-8, 4px);
    border: var(--1-2, 1px) solid
      var(--std-Neut-x-048, rgba(215, 215, 215, 0.48));
    background: var(--Surface-Cumulative-plus16, rgba(255, 255, 255, 0.64));
    box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(12px);
  }
}
</style>
